/**
 * ゲームバランス定数の**単一の置き場**(CLAUDE.md ルール3 / docs/02_game_design.md)。
 *
 * ここ以外の場所に数値を直書きしないこと。プレイテストでの調整はこのファイルだけを触る。
 * 初期値は Unity 版(`ポイント制度の導入` ブランチ)の実装値を docs/02 が読み取ったものを採用する。
 */

import type { Difficulty, PerformanceStage, TypingLanguage } from './types.js';

/** スコアリング定数(docs/02 §5)。 */
export const SCORING = {
  /** baseScore = base + 累計正解打鍵数 × perCorrect(docs/02 §5.2)。 */
  baseScore: 1,
  /** Unity版は 0.2 だったが docs/02 §5.2 の確定値は 0.1。 */
  baseScorePerCorrect: 0.1,

  /** コンボ倍率の基準値。 */
  comboBonusBase: 1.0,
  /** 何コンボごとに倍率が上がるか。 */
  comboStep: 10,
  /** ステップごとの加算単位。10コンボ目ごとに `+(combo/step) × unit`。 */
  comboBonusUnit: 0.1,
  /** 【新規】コンボ倍率の上限(Unity版は無上限)。 */
  comboBonusCap: 3.0,

  /** ゲージ最大値(%)。 */
  gaugeMax: 100,
  /** ゲージ最小値(%)。 */
  gaugeMin: 0,
  /** 正解打鍵1回あたりのゲージ増加(%)。 */
  gaugeGainPerCorrect: 10,

  /**
   * クリア時コイン = min(floor(score × coinRate[言語]), coinMaxPerClear)(docs/02 §5.7)。
   *
   * **言語別**にしてあるのは、日本語(とくに文章ステージ)のスコアの伸び方が
   * 英語の単語ステージと桁違いだから。一律5%だと日本語の文章1クリアで約2,000コインとなり、
   * まなび(20〜40/回)・パズル(日次300上限)・ガチャ1回100 と釣り合わなくなる。
   * 日本語だけ 1/5 の 0.01 に下げ、英語は 0.05 のまま据え置く。
   */
  coinRate: { japanese: 0.01, english: 0.05 } as Readonly<Record<TypingLanguage, number>>,
  /**
   * 1クリアで得られるクリア報酬の上限(全言語共通の安全弁)。
   * ハイスコアチャレンジ等で極端なスコアが出ても、ここで頭打ちにする。
   * 初回星ボーナス(ステージ定義の `starCoins`)はこの上限の対象外。
   */
  coinMaxPerClear: 500,
} as const;

/**
 * ゲージ→倍率テーブル(docs/02 §5.4。Unity版30段テーブルを4段に整理)。
 * `maxGauge` 以下ならその倍率。上から順に評価する。
 */
export const GAUGE_MULTIPLIER_TABLE: readonly {
  readonly maxGauge: number;
  readonly multiplier: number;
}[] = [
  { maxGauge: 33, multiplier: 1 },
  { maxGauge: 63, multiplier: 2 },
  { maxGauge: 83, multiplier: 3 },
  { maxGauge: Number.POSITIVE_INFINITY, multiplier: 4 },
];

/** 演出段階の閾値(docs/02 §5.5)。閾値を **超えた** ら次の段階。 */
export const PERFORMANCE_THRESHOLDS: Readonly<Record<Exclude<PerformanceStage, 'normal'>, number>> =
  {
    groove: 33,
    fever: 83,
  };

/** ローマ字ガイドの表示方式(docs/02 §2)。 */
export type RomajiGuideMode = 'full' | 'preview' | 'typedOnly';
/** キーボード+指ガイドの表示方式(docs/02 §2)。 */
export type KeyboardGuideMode = 'always' | 'untilFirstInput' | 'hidden';

/** 難易度別の設定。 */
export interface DifficultySettings {
  /** キーボード+指ガイド。 */
  readonly keyboardGuide: KeyboardGuideMode;
  /** ローマ字ガイド。 */
  readonly romajiGuide: RomajiGuideMode;
  /** `romajiGuide === 'preview'` のときの表示時間(ms)。 */
  readonly romajiPreviewMs: number;
  /** 英語モードで問題の綴りを表示するか(false=リスニングタイピング)。 */
  readonly showEnglishWord: boolean;
  /** 入力が止まってからヒントを再表示するまでの時間(ms)。0=再表示しない。 */
  readonly hintIdleMs: number;
  /**
   * 英語モードで綴りを伏せているとき(`showEnglishWord === false`)、
   * 助け舟として正解の綴り+キーボードガイドを出すまでの無操作時間(ms)。
   * 0 = 助け舟を出さない(むずかしい)。ミスタイプした時点でも同じ助け舟を出す。
   */
  readonly englishAssistIdleMs: number;
  /** ゲージ減衰(%/0.1秒)(docs/02 §5.4)。 */
  readonly gaugeDecayPer100ms: number;
  /** 【新規】難易度スコア倍率(docs/02 §2)。 */
  readonly scoreMultiplier: number;
}

/** 難易度別設定テーブル(docs/02 §2 / §5.4)。 */
export const DIFFICULTY_SETTINGS: Readonly<Record<Difficulty, DifficultySettings>> = {
  easy: {
    keyboardGuide: 'always',
    romajiGuide: 'full',
    romajiPreviewMs: 0,
    showEnglishWord: true,
    hintIdleMs: 0,
    englishAssistIdleMs: 0,
    gaugeDecayPer100ms: 0.1,
    scoreMultiplier: 1.0,
  },
  normal: {
    keyboardGuide: 'untilFirstInput',
    romajiGuide: 'preview',
    romajiPreviewMs: 1000,
    showEnglishWord: false,
    hintIdleMs: 2000,
    englishAssistIdleMs: 3000,
    gaugeDecayPer100ms: 0.8,
    scoreMultiplier: 1.2,
  },
  hard: {
    keyboardGuide: 'hidden',
    romajiGuide: 'typedOnly',
    romajiPreviewMs: 0,
    showEnglishWord: false,
    hintIdleMs: 0,
    englishAssistIdleMs: 0,
    gaugeDecayPer100ms: 1.0,
    scoreMultiplier: 1.5,
  },
};

/** 難易度設定を取得する。 */
export function getDifficultySettings(difficulty: Difficulty): DifficultySettings {
  return DIFFICULTY_SETTINGS[difficulty];
}

/**
 * 星の判定条件(docs/02 §5.6)。累積条件。
 * `missRateBelow` は「未満」、`kpmAtLeast` は「以上」。
 */
export const STAR_THRESHOLDS = {
  /** ★2: ミス率10%未満。 */
  star2MissRateBelow: 0.1,
  /** ★3: ミス率5%未満。 */
  star3MissRateBelow: 0.05,
  /** ★4: ★3 かつ 60打/分以上。 */
  star4KpmAtLeast: 60,
  /** ★5: ミス率3%未満 かつ 90打/分以上。 */
  star5MissRateBelow: 0.03,
  star5KpmAtLeast: 90,
  /** 星の最大数(Unity版3→Web版5)。 */
  maxStars: 5,
} as const;

/** ステージの手動解除・卒業に関する定数(docs/02 §6.4)。 */
export const TYPING_STAGE_RULES = {
  /** 母国語ステージの同一難易度を卒業するまでの★5クリア回数。 */
  nativeFiveStarClearsToGraduate: 3,
} as const;

/** アイテム効果(ScoreModifier)の上限など。 */
export const MODIFIER_LIMITS = {
  /** ゲージ減衰軽減率の上限(0〜1)。 */
  maxGaugeDecayReduction: 0.9,
  /** コンボ上限引き上げの上限(comboBonusCap への加算)。 */
  maxComboCapUp: 2.0,
  /** ミス見逃し回数の上限(1セッションあたり)。 */
  maxMissForgive: 5,
  /** コイン獲得率アップの上限(加算)。 */
  maxCoinBonus: 1.0,
} as const;

/** 出題まわりの既定値。 */
export const QUIZ_DEFAULTS = {
  repeatCount: 1,
  timeLimitSec: 60,
} as const;
